import React from 'react'
import clsx from 'clsx'
import { htmlToPlainText } from 'libs/text'
import { Skeleton } from '@material-ui/lab'
import momentTimezone from 'moment-timezone'
import { Icon, Typography, withStyles } from '@material-ui/core'

import StatusBadge from 'app/componentV4/Status/Badge'
import { creditAi } from 'app/main/plansAndBilling/constants'
import LightTooltip from 'app/componentV4/Tooltip/LightTooltip'
import PrimaryTooltip from 'app/componentV4/Tooltip/PrimaryTooltip'
import {
  SmsIcon,
  EmailIcon,
  AutomateCallIcon,
  SocialMedia,
  StarPlusIcon,
} from 'app/components/icons'
import { getLocalizedDate } from 'commons/date'

export const handleText = (type, count, formatMessage) => {
  switch (type) {
    case creditAi.GENERATE_CONTENT:
      return formatMessage(
        {
          id: 'planAndBilling.usageHistory.ai.type.content',
        },
        { count: count },
      )
    case creditAi.GENERATE_IMAGE:
      return formatMessage(
        { id: 'planAndBilling.usageHistory.ai.type.image' },
        { count: count },
      )
    case creditAi.TEXT_SUMMARIZE:
      return formatMessage(
        {
          id: 'planAndBilling.usageHistory.ai.type.summarized',
        },
        { count: count },
      )
    case creditAi.TEXT_REVISE:
      return formatMessage(
        { id: 'planAndBilling.usageHistory.ai.type.revise' },
        { count: count },
      )
    case creditAi.TEXT_RATING:
      return formatMessage(
        { id: 'planAndBilling.usageHistory.ai.type.rating' },
        { count: count },
      )
    default:
      return ''
  }
}

const CustomLightTooltip = withStyles({
  tooltip: {
    width: '350px',
  },
})(LightTooltip)

const renderCredit = (value) => {
  return value > 0 ? (
    <Typography
      className="text-grey-400 text-16 font-400"
      style={{ width: '5vw' }}
    >
      {value}
    </Typography>
  ) : (
    '-'
  )
}

const renderAiCredit = (data, formatMessage) => {
  return (
    <div className="p-5 space-y-5">
      {data.map((item, index) => (
        <Typography
          key={`${item.GENERATE_CONTENT}-${index}`}
          className="text-primary text-14 flex justify-between"
        >
          <span>{handleText(item.type, item.count, formatMessage)}</span>
          <span>{item.credits}</span>
        </Typography>
      ))}
    </div>
  )
}

const handleAiCredit = (loading, data, formatMessage) => {
  return loading ? (
    <div className="p-5 space-y-5">
      <Skeleton animation="wave" variant="text" style={{ height: '20px' }} />
      <Skeleton animation="wave" variant="text" style={{ height: '20px' }} />
      <Skeleton animation="wave" variant="text" style={{ height: '20px' }} />
      <Skeleton animation="wave" variant="text" style={{ height: '20px' }} />
      <Skeleton animation="wave" variant="text" style={{ height: '20px' }} />
    </div>
  ) : (
    renderAiCredit(data, formatMessage)
  )
}

export const ColumnOfData = (
  formatMessage,
  appLogo,
  place,
  handleFetchAiPreview,
  isLoadingAi,
  aiCredit,
  appName,
) => [
  {
    header: formatMessage({
      id: 'message.title.label',
    }),
    accessor: 'messageTitle',
    style: { width: '30vw' },
    sort: true,
    render: (row) => (
      <div style={{ width: '30vw' }}>
        <div className="flex space-x-5 items-center">
          <Typography className="text-12 text-grey-200">
            {getLocalizedDate(row.createdAt, 'DATE_TIME')}
          </Typography>
          {row.isBeacon ? (
            <StatusBadge
              label={formatMessage({
                id: 'beacons.table.search.label',
              })}
              type={'ACCENT'}
            />
          ) : null}
        </div>
        <Typography className="text-16 text-grey-400 font-bold truncate">
          {htmlToPlainText(row.title)}
        </Typography>
        <Typography className="text-16 text-grey-400 truncate">
          {row.descriptionPreview}
        </Typography>
      </div>
    ),
  },
  {
    header: (
      <PrimaryTooltip
        title={formatMessage(
          {
            id: 'subscriber.filter.notification.method.app',
          },
          { appName: appName },
        )}
      >
        <Icon>
          <img src={appLogo} width={23} alt="app" />,
        </Icon>
      </PrimaryTooltip>
    ),

    accessor: 'appCount',
    style: { width: '5vw' },
    sort: true,
    render: (row) => {
      return renderCredit(row.credits.appCount)
    },
  },
  {
    header: (
      <PrimaryTooltip
        title={formatMessage({
          id: 'channel.sms',
        })}
      >
        <Icon>
          <SmsIcon
            color={'#00B2AD'}
            classes="w-24 h-24"
            notBackground={false}
            backgroundColor={'#183560'}
          />
        </Icon>
      </PrimaryTooltip>
    ),
    accessor: 'smsCount',
    style: { width: '5vw' },
    sort: true,
    render: (row) => {
      return renderCredit(row.credits.smsCount)
    },
  },
  {
    header: (
      <PrimaryTooltip
        title={formatMessage({
          id: 'channel.email',
        })}
      >
        <Icon>
          <EmailIcon
            color={'#00B2AD'}
            classes="w-24 h-24"
            notBackground={false}
            backgroundColor={'#183560'}
          />
        </Icon>
      </PrimaryTooltip>
    ),
    accessor: 'emailCount',
    style: { width: '5vw' },
    sort: true,
    render: (row) => {
      return renderCredit(row.credits.emailCount)
    },
  },
  {
    header: (
      <PrimaryTooltip
        title={formatMessage({
          id: 'channel.socialMedia',
        })}
      >
        <Icon>
          <SocialMedia
            color={'#00B2AD'}
            classes="w-24 h-24"
            notBackground={false}
            backgroundColor={'#183560'}
          />
        </Icon>
      </PrimaryTooltip>
    ),
    accessor: 'socialCount',
    style: { width: '5vw' },
    sort: true,
    render: (row) => {
      return renderCredit(row.credits.socialCount)
    },
  },
  {
    header: (
      <PrimaryTooltip
        title={formatMessage({
          id: 'channel.automatedCall',
        })}
      >
        <Icon>
          <AutomateCallIcon
            color={'#00B2AD'}
            classes={clsx('w-24 h-24')}
            notBackground={false}
            backgroundColor={'#183560'}
          />
        </Icon>
      </PrimaryTooltip>
    ),
    disabled: !place.enabledAutomatedCall,
    accessor: 'automatedCallCount',
    style: { width: '5vw' },
    sort: place.enabledAutomatedCall === 1,
    render: (row) => {
      return renderCredit(row.credits.automatedCallCount)
    },
  },
  {
    header: (
      <PrimaryTooltip
        title={formatMessage({
          id: 'ai.lable',
        })}
      >
        <Icon
          className={clsx(
            'bg-primary rounded-full w-24 h-24 flex items-center justify-center ',
          )}
        >
          <StarPlusIcon color={'#00B2AD'} />
        </Icon>
      </PrimaryTooltip>
    ),
    disabled: !place.enabledAi,
    accessor: 'aiCount',
    style: { width: '5vw' },
    sort: place.enabledAi === 1,
    render: (row) => (
      <>
        {row.credits.aiCount !== 0 ? (
          <CustomLightTooltip
            placement="bottom-start"
            title={handleAiCredit(isLoadingAi, aiCredit, formatMessage)}
            className="w-full"
          >
            <div>
              <Typography
                className="text-sky-600 text-16 font-500"
                onMouseOver={() => handleFetchAiPreview(row.requestId)}
                // onMouseOut={() => {}}
              >
                {row.credits.aiCount}
              </Typography>
            </div>
          </CustomLightTooltip>
        ) : (
          '-'
        )}
      </>
    ),
  },
]
